import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { engineService } from '@/services/engine-service';
import type { Evaluator } from '@/types/core/workflow-rule';
import type { Page } from '@/types/shared/page';
import { pollForResourceGone } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { engineQueryKeys, evaluatorMutationKeys } from './query-keys';

/**
 * Hook to delete an evaluator with optimistic updates.
 * Optimistically removes the evaluator from cache, then polls until it is gone.
 */
export function useDeleteEvaluator(tenantId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: evaluatorMutationKeys.deleteEvaluator(tenantId),
    mutationFn: async (ruleId: UUID) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const success = await engineService.deleteEvaluator(tenantId, ruleId);
      if (!success) {
        throw new Error('Failed to delete evaluator');
      }

      await pollForResourceGone(() =>
        engineService.findEvaluator(tenantId, ruleId)
      );
      return ruleId;
    },
    onMutate: async (ruleId: UUID) => {
      if (!tenantId) return { previousData: [] };

      await qc.cancelQueries({
        queryKey: engineQueryKeys.evaluatorListByTenant(tenantId),
      });

      const previousData = qc.getQueriesData<InfiniteData<Page<Evaluator>>>({
        queryKey: engineQueryKeys.evaluatorListByTenant(tenantId),
      });

      qc.setQueriesData<InfiniteData<Page<Evaluator>>>(
        { queryKey: engineQueryKeys.evaluatorListByTenant(tenantId) },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page, index) => ({
              ...page,
              items: page.items.filter((e) => e.ruleId !== ruleId),
              total: index === 0 ? (page.total ?? 0) - 1 : page.total,
            })),
          };
        }
      );

      return { previousData };
    },
    onError: (_error, _ruleId, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          qc.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: engineQueryKeys.evaluatorListByTenant(tenantId),
        refetchType: 'all',
      });
    },
  });
}
